import React, { useEffect, useRef, useState } from 'react';
import { carteraApi } from './api';
import { Pill } from './helpers';

/**
 * Línea de "la cotización cambió y el lead no lo sabe", con el botón Avisarle.
 *
 * Se pide al montar y cada vez que cambia la conversación. Sin cambio pendiente,
 * o si el GET falla, no pinta nada: es un apoyo, nunca debe estorbar la tarjeta.
 *
 * 🔴 Con la ventana de 24 h cerrada no se puede escribir: el botón queda
 * deshabilitado y el aviso sale solo cuando el lead escriba (lo hace el bot).
 *
 * @param {object} props
 * @param {number} props.conversacionId
 * @param {() => (void|Promise<any>)} props.onAvisado  el padre refresca hilo y tarjeta
 */
export const CambioCotizacionAviso = ({ conversacionId, onAvisado }) => {
  const [estado, setEstado] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resultado, setResultado] = useState(null);
  // Conversación vigente: una respuesta tardía de otra conversación se ignora.
  const conversacionRef = useRef(conversacionId);

  useEffect(() => {
    conversacionRef.current = conversacionId;
    // Se ignora la respuesta vieja si el asesor cambió de conversación en medio.
    let vigente = true;
    const controlador = new AbortController();
    setEstado(null);
    setResultado(null);
    setBusy(false);

    if (conversacionId) {
      carteraApi.cambioCotizacion(conversacionId, controlador.signal)
        .then((res) => { if (vigente) setEstado(res?.data || null); })
        .catch(() => { if (vigente) setEstado(null); });
    }

    return () => {
      vigente = false;
      controlador.abort();
    };
  }, [conversacionId]);

  const avisar = async () => {
    if (busy || !estado?.puede_avisar) return;
    const id = conversacionId;
    const sigueVigente = () => conversacionRef.current === id;
    setBusy(true);
    setResultado(null);
    try {
      await carteraApi.avisarCambioCotizacion(id);
      if (!sigueVigente()) return;
      setResultado({ ok: true, texto: 'Aviso enviado · el lead ya tiene los números nuevos' });
      setEstado(null);
      if (onAvisado) await onAvisado();
    } catch (e) {
      if (!sigueVigente()) return;
      if (e?.response?.status === 409 && e?.response?.data?.codigo === 'sin_cambio') {
        // Ya avisó otro turno/clic: se repinta con el estado real.
        try {
          const res = await carteraApi.cambioCotizacion(id);
          if (sigueVigente()) setEstado(res?.data || null);
        } catch (_) {
          if (sigueVigente()) setEstado(null);
        }
      } else {
        setResultado({ ok: false, texto: `No se pudo avisar: ${motivoLegible(e)}` });
      }
    } finally {
      if (sigueVigente()) setBusy(false);
    }
  };

  if (resultado?.ok) {
    return (
      <div role="status" style={{ marginTop: 9, fontSize: 11, color: '#087A4D', fontWeight: 600 }}>
        {resultado.texto}
      </div>
    );
  }

  if (!estado?.pendiente) return null;

  const ahora = estado.ahora || {};
  const cerrada = !estado.puede_avisar && estado.motivo === 'ventana_cerrada';
  const deshabilitado = busy || !estado.puede_avisar;

  return (
    <div style={{
      marginTop: 9, padding: '8px 10px', borderRadius: 9,
      background: '#FFF6E5', border: '1px solid #F4E2BC', textAlign: 'left',
    }}>
      <div style={{ color: '#8a6d2a', fontSize: 11, fontWeight: 700, lineHeight: 1.35 }}>
        ⚠ La cotización cambió y el lead no lo sabe.
      </div>
      <div style={{ color: '#8a6d2a', fontSize: 11, lineHeight: 1.35, marginTop: 2 }}>
        Ahora: {[ahora.plazo, ahora.pago_inicial, ahora.renta].filter(Boolean).join(' · ')}
      </div>
      <Pill
        icon={busy ? 'pi-spin pi-spinner' : 'pi-send'}
        disabled={deshabilitado}
        onClick={avisar}
        style={{ width: '100%', marginTop: 8 }}
      >
        {cerrada ? 'Se le avisará cuando escriba' : 'Avisarle'}
      </Pill>
      {resultado && !resultado.ok && (
        <div role="alert" style={{ marginTop: 6, fontSize: 11, color: '#B42318', lineHeight: 1.35 }}>
          {resultado.texto}
        </div>
      )}
    </div>
  );
};

/** El texto del backend ya viene en español; sin él, un genérico. */
const motivoLegible = (error) => {
  const data = error?.response?.data;
  if (data?.mensaje || data?.message) return data.mensaje || data.message;
  if (data?.codigo === 'ventana_cerrada') return 'la ventana de 24 h está cerrada.';
  return 'revisa tu conexión y vuelve a intentar.';
};
